/**
 * S6 — Locked Feature Full-Panel Empty State
 *
 * Shown when the user navigates into a gated surface (AI Scribe panel, Reports page)
 * instead of letting the API 402. Reuses the patient-list empty-state pattern.
 *
 * Locked · Admin:       lock icon + "AI Scribe" + "View plans"
 * Locked · Non-admin:   lock icon + "AI Scribe" + "Notify admin"
 * Trial active:         shows feature normally but with trial banner inside panel
 *
 * Figma: S6 — Locked Feature Full-Panel Empty State
 */
import React from "react";

interface LockedFeatureEmptyStateProps {
  featureLabel: string;
  description: string;
  isAdmin: boolean;
  variant: "locked" | "expired";
  expiredDate?: string | null;
  onUpgradeClick: () => void;
  onNotifyAdmin: () => void;
}

export const LockedFeatureEmptyState: React.FC<LockedFeatureEmptyStateProps> = ({
  featureLabel,
  description,
  isAdmin,
  variant,
  expiredDate,
  onUpgradeClick,
  onNotifyAdmin,
}) => {
  const isExpired = variant === "expired";

  const formattedDate = expiredDate
    ? new Date(expiredDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : null;

  return (
    <div className="flex flex-col items-center justify-center py-10 sm:py-16 px-4 sm:px-6 text-center">
      {/* Icon */}
      <div className="mb-4 sm:mb-5 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-slate-100 flex items-center justify-center">
        {isExpired ? (
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 22h14M5 2h14M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22M17 2v4.172a2 2 0 0 1-.586 1.414L12 12l-4.414-4.414A2 2 0 0 1 7 6.172V2" />
          </svg>
        ) : (
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        )}
      </div>

      {/* Feature name */}
      <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">{featureLabel}</h3>

      {/* Description */}
      <p className="text-xs sm:text-sm text-slate-500 mb-5 sm:mb-6 max-w-xs">
        {isExpired
          ? `Your ${featureLabel} trial ended${formattedDate ? ` on ${formattedDate}` : ""}. Subscribe to continue using this feature.`
          : description}
      </p>

      {/* CTA */}
      <button
        onClick={isAdmin ? onUpgradeClick : onNotifyAdmin}
        className="px-5 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
      >
        {isAdmin ? "View plans" : "Notify admin"}
      </button>
    </div>
  );
};

/**
 * Trial-active inline banner — shown inside the feature panel when trial is active.
 * "AI Scribe trial ends in 6 days." (inside the panel, not global)
 */
export const TrialActiveBanner: React.FC<{
  featureLabel: string;
  daysLeft: number;
}> = ({ featureLabel, daysLeft }) => {
  const urgent = daysLeft <= 2;
  return (
    <div className={`w-full rounded-t-xl px-3 sm:px-4 py-1.5 sm:py-2 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium ${
      urgent ? "bg-red-50 text-red-700 border-b border-red-200" : "bg-blue-50 text-blue-700 border-b border-blue-200"
    }`}>
      <span>⏳</span>
      <span>{featureLabel} trial ends in {daysLeft} day{daysLeft !== 1 ? "s" : ""}.</span>
    </div>
  );
};

export default LockedFeatureEmptyState;
